import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XIcon, ArrowRightIcon, StarIcon } from 'lucide-react';
import { Button } from '../ui/Button';
const comparisonData = [{
  feature: 'Built-in database of 200M+ local businesses',
  openmart: true,
  generic: false
}, {
  feature: 'Natural language querying (no complex filters)',
  openmart: true,
  generic: false
}, {
  feature: 'Automated lead scoring and prioritization',
  openmart: true,
  generic: true
}, {
  feature: 'AI-drafted personalized outreach emails',
  openmart: true,
  generic: false
}, {
  feature: 'Built-in multi-step email sequencing & follow-ups',
  openmart: true,
  generic: false
}, {
  feature: 'Designed specifically for local business prospecting',
  openmart: true,
  generic: false
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const rowVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
export function OpenclawComparison() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5285">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-5286">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-50/50 rounded-full blur-3xl -z-10 pointer-events-none" data-id="element-5287" />

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-5288">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-5289">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-5290">
              Why sales teams choose{' '}
              <span className="text-blue-600" data-id="element-5291">Openclaw for sales</span>
            </h2>
            <p className="text-lg text-slate-500 leading-relaxed" data-id="element-5292">
              Compare our AI sales agent against traditional prospecting tools
              and manual research.
            </p>
          </div>

          <div className="relative" data-id="element-5293">
            <div className="grid grid-cols-[2fr_1fr_1fr] md:grid-cols-[3fr_1.5fr_1.5fr] items-end mb-4 px-4 md:px-8" data-id="element-5294">
              <div className="pb-6" data-id="element-5295">
                <h3 className="text-xl font-bold text-slate-900" data-id="element-5296">
                  Feature comparison
                </h3>
              </div>
              <div className="pb-6 text-center relative z-10" data-id="element-5297">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-5298">
                  <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-5299" /> Recommended
                </div>
                <h3 className="text-xl font-bold text-slate-900" data-id="element-5300">
                  Openclaw for sales
                </h3>
              </div>
              <div className="pb-6 text-center" data-id="element-5301">
                <h3 className="text-lg font-semibold text-slate-500" data-id="element-5302">
                  Traditional Tools
                </h3>
              </div>
            </div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} className="relative" data-id="element-5303">
              <div className="absolute top-0 bottom-0 bg-slate-50/80 rounded-[2rem] border border-slate-100 shadow-sm -z-10" style={{
              left: 'calc(50% + 0px)',
              width: '25%'
            }} data-id="element-5304" />
              <div className="flex flex-col" data-id="element-5305">
                {comparisonData.map((row, index) => {
                const isFirst = index === 0;
                const isLast = index === comparisonData.length - 1;
                return <motion.div key={index} variants={rowVariants} className="grid grid-cols-[2fr_1fr_1fr] md:grid-cols-[3fr_1.5fr_1.5fr] items-center group transition-colors hover:bg-slate-50/50 rounded-2xl" data-id="element-5306">
                      <div className="py-5 px-4 md:px-8 border-b border-slate-100 group-last:border-0" data-id="element-5307">
                        <span className="text-lg font-medium text-slate-700" data-id="element-5308">
                          {row.feature}
                        </span>
                      </div>
                      <div className={`py-5 px-2 flex justify-center items-center bg-slate-50/80 border-x border-slate-100/50 relative z-10 ${isFirst ? 'rounded-t-[2rem] border-t' : ''} ${isLast ? 'rounded-b-[2rem] border-b shadow-sm' : ''} ${!isLast ? 'border-b border-slate-100/50' : ''}`} data-id="element-5309">
                        {row.openmart ? <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-5310">
                            <CheckIcon className="w-5 h-5 text-blue-600" strokeWidth={3} data-id="element-5311" />
                          </div> : <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center" data-id="element-5312">
                            <XIcon className="w-5 h-5 text-slate-300" strokeWidth={2} data-id="element-5313" />
                          </div>}
                      </div>
                      <div className="py-5 px-2 flex justify-center items-center border-b border-slate-100 group-last:border-0" data-id="element-5314">
                        {row.generic ? <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center" data-id="element-5315">
                            <CheckIcon className="w-5 h-5 text-slate-400" strokeWidth={2} data-id="element-5316" />
                          </div> : <div className="w-8 h-8 rounded-full flex items-center justify-center" data-id="element-5317">
                            <XIcon className="w-5 h-5 text-slate-200" strokeWidth={2} data-id="element-5318" />
                          </div>}
                      </div>
                    </motion.div>;
              })}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.8,
            duration: 0.5
          }} className="mt-12 text-center" data-id="element-5319">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-5320">
                <Button href="http://app.openmart.com/register" variant="primary" size="lg" arrow data-id="element-5321">
                  Try for free
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}